// useTraceSignalHighlights - resolve the persisted Signal evidence for a trace
// into span highlights, then run the client-only kernels that have no backend
// producer. Every catalog detection's spans come from `signal_tags` evidence and
// are digest-verified before rendering (persistedSignalEvidence.js); the
// dashboard never reruns a detector in the browser and carries no detector
// copies. A row whose evidence is legacy, malformed, truncated, or unresolvable
// lists in the Signals tab/console/badges but has no span highlight here, and
// the trace view says so (`evidenceStatus`).
//
// A standalone hook (not inline in ContextPanel) so the fetch/resolve lifecycle
// is unit-testable in isolation (the component itself is untested). Independent
// of signals-catalog hydration: the namespace on each row is enough to attribute
// a mark, so a trace opened before the catalog lands gets its highlights on the
// first pass.
//
// Re-runs when the trace's turns change, not only when its id does. Locators
// are turn/block indices into the rendered axis, and that axis is not fixed for
// the life of a tab: the conversation record before the capture folds in after
// the trace opens (completeTrace prepends it), and the session axis loads in
// pages. Marks resolved against the earlier array would then point at the wrong
// turns, so the resolution follows the array.
import { useEffect, useState } from "react";
import { hydratePrompt, runTrigger, isCheapTrigger } from "./triggers/triggerHarness.js";
import { traceScanSurfaces } from "./triggers/promptSurfaces.js";
import { CLIENT_ONLY_TRIGGERS } from "./triggers/exampleTriggers.js";
import { getSignalTags } from "./analyticsClient.js";
import { DETECTION_NAMESPACES, persistedSignalHighlights } from "./persistedSignalEvidence.js";

export function useTraceSignalHighlights(trace) {
  const [result, setResult] = useState({ highlights: null, evidenceStatus: "loading" });
  const traceId = trace?.id ?? null;
  const traceTurns = Array.isArray(trace?.turns) ? trace.turns : null;
  useEffect(() => {
    let live = true;
    setResult({ highlights: null, evidenceStatus: "loading" });
    if (!traceId) return undefined;
    const clientOnlyTriggers = CLIENT_ONLY_TRIGGERS.filter((t) => t && isCheapTrigger(t));
    (async () => {
      try {
        // The trace on screen IS the hydrated body: hand its turns and tool
        // catalog over rather than reading the whole conversation a second time.
        const preloaded = Array.isArray(trace?.turns) && trace.turns.length
          ? {
            metadata: {
              event_id: traceId, endpoint_id: trace.endpointId ?? null, session_id: trace.sessionId ?? null,
              // The raw server string, as the query path carries it; `timestamp` is
              // the parsed number the list sorts by.
              event_time: trace.eventTime || null, provider: trace.provider || "", model: trace.model || "",
            },
            parsed: { turns: trace.turns, toolDefs: trace.toolDefs || [], partialBody: Boolean(trace.partialBody) },
          }
          : null;
        const [event, persistedRows] = await Promise.all([
          preloaded ? hydratePrompt(traceId, preloaded) : hydratePrompt(traceId),
          getSignalTags([traceId], null, {
            namespaces: DETECTION_NAMESPACES,
            includeEvidence: true,
          }).catch(() => null),
        ]);
        if (!live) return;
        if (!event) {
          const evidenceStatus = persistedRows?.length ? "unavailable" : "complete";
          setResult({ highlights: [], evidenceStatus });
          return;
        }
        const persisted = persistedRows == null
          ? { highlights: [], status: "unknown" }
          : await persistedSignalHighlights(persistedRows, trace, event);
        const fired = [...persisted.highlights];
        // The token-efficiency signal anchors its locs to (turnIndex, blockIndex)
        // over the rendered turns, so run it on an event whose `turns` IS
        // `trace.turns` — the same array buildHighlightMap maps against.
        if (clientOnlyTriggers.length) {
          const expEvent = { ...event, ...traceScanSurfaces(trace?.turns || []), turns: trace?.turns || [] };
          // Sequential on purpose: the realm worker runs kernels one at a time on a
          // single Pyodide instance, so awaiting concurrently would only reorder
          // `fired` without draining the queue any faster.
          for (const trig of clientOnlyTriggers) {
            const r = await runTrigger(trig, expEvent);
            if (r && r.fired && r.annotation && live) {
              fired.push({ command: trig.command, type: r.annotation.type, value: r.annotation.value ?? null });
            }
          }
        }
        if (live) setResult({ highlights: fired, evidenceStatus: persisted.status });
      } catch {
        if (live) setResult({ highlights: [], evidenceStatus: "unknown" });
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceId, traceTurns]);
  return result;
}
